/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

document.addEventListener('variant:changed', function(event) {
  let variant = event.detail.variant;
  let price = variant.price
  let priceholder = document.getElementById('productprice');
  let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
  if (variant.compare_at_price) {
    priceholder.innerHTML = `<span class="price price--highlight price--large">${formatMoney(variant["price"], currencyFormat)}</span><span class="price price--compare">${formatMoney(variant["compare_at_price"], currencyFormat)}</span>`;  
  } else {
    priceholder.innerHTML = `<span class="price price--highlight price--large">${formatMoney(variant["price"], currencyFormat)}</span>`;  
  }
});


// If secondary hover image is a video, play on hover
const imageWrappers = document.querySelectorAll(".product-item__image-wrapper");

imageWrappers.forEach((wrapper) => {
  const video = wrapper.querySelector(".product-item__secondary-video video");

  wrapper.addEventListener("mouseover", () => {
    if (video) {
      video.play();
    }
  });
});


// Hide a second set of variant options if more than one exists
(function(){

    // Get all items with classes "block-swatch__item" and "color-swatch__item"
    const swatchItems = document.querySelectorAll(".block-swatch__item, .color-swatch__item");
  
    // Loop through all variant options
    swatchItems.forEach((item) => {
      // Add a click event to each item
      item.addEventListener("click", () => {
        // Get all divs that have variant options in them
        const optionSelectors = document.querySelectorAll(".product-form__option-selector");
  
        // Set all the divs with variant options to block, so all variant options are shown after the first selection
        optionSelectors.forEach((selector) => {
          selector.style.display = "block";
        });
      });
    });
  
})();

// The sticky add-to-cart bar used to be hidden on load and only revealed once a
// swatch was clicked. That also hid the express payment button it contains, so
// on mobile - where the main Add to Cart sits well below the fold on a long
// product page - there was frequently no buy action on screen at all. The bar
// now stays visible.
//
// It can't simply be shown, though. forceVariantSelection() below deliberately
// clears the option swatches so customers choose consciously, but the form still
// carries a default variant id, so buying from the sticky bar in that state
// would order the wrong fitment. So mirror the main button's disabled state onto
// the sticky one, and keep express checkout out of reach until a real choice has
// been made - express pay skips variant selection entirely.
(function () {
    function syncStickyBuyState() {
        var main = document.querySelector('#AddToCart');
        var sticky = document.querySelector('#StickyAddToCart');
        if (!main || !sticky) return;

        var awaitingChoice = main.disabled;
        sticky.disabled = awaitingChoice;

        // The theme fills these labels in after DOMContentLoaded, so only copy
        // once there is something to copy - otherwise the sticky button ends up
        // visible with no text at all.
        var mainText = main.querySelector('.loader-button__text');
        var stickyText = sticky.querySelector('.loader-button__text');
        if (mainText && stickyText && mainText.innerText.trim()) {
            stickyText.innerText = mainText.innerText;
        }

        // Target the express buttons themselves, NOT product-payment-container -
        // that wrapper also holds the Add to Cart button, so hiding it removes
        // every way to buy.
        document.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach(function (pay) {
            pay.style.display = awaitingChoice ? 'none' : '';
        });
    }

    function start() {
        if (!document.querySelector('.product-sticky-form')) return;
        syncStickyBuyState();

        // The theme re-enables the main button once every option has a value.
        document.addEventListener('variant:changed', syncStickyBuyState);

        // Watch the label as well as the disabled attribute: the theme sets both
        // asynchronously, and the sticky bar has to follow either change.
        var main = document.querySelector('#AddToCart');
        if (main) {
            new MutationObserver(syncStickyBuyState).observe(main, {
                attributes: true,
                attributeFilter: ['disabled'],
                childList: true,
                subtree: true,
                characterData: true
            });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();


// Force variant selection
// Allow default selection for the color option, but any other options will require the customer
// to select them explicitely.
// This is done by examining each product option swatch, checking the option name, and if it's not 'color', unselecting.
// A change event handler on the swatch elements will re-enable the add to cart button if all have a selection.
function forceVariantSelection() {
    let optionSelectors = document.querySelectorAll(".product-form__option-selector");
    // track if we actually uncheck any of the option swatches
    let uncheckedOptions = false;
  
    // Exit only if there is genuinely nothing to choose. Disabling Add to Cart
    // on a product with no options would strand the customer with no way to
    // enable it.
    //
    // This used to read `< 2`, which skipped every single-option product — so
    // on something like the Model 3/Y mud flaps ("Model" being the only
    // option) Add to Cart stayed lit with a variant pre-selected, and
    // customers were ordering the wrong one. Two-option products such as the
    // Cybertruck puddle lights were unaffected, which is why the bug looked
    // inconsistent.
    if(optionSelectors.length < 1) {
      return;
    }

    // 🔴 Nothing to force when there is only one possible combination.
    //
    // Reported by a customer 2026-08-20 on the Rivian R2 SnapPlate: the year
    // swatch could be clicked but the button stayed on "Make a Selection"
    // forever, so the product could not be bought at all. NINE active, in-stock
    // products were in this state.
    //
    // The cause is that re-enabling Add to Cart depends on the theme firing
    // `variant:changed`, and selecting the variant the page ALREADY defaulted to
    // does not fire it. forceSwatchSelection() exists to work around exactly
    // that, by bumping to variants[1] to force a change - but it is guarded on
    // `variants.length > 1`, and with a single variant there is no variants[1].
    // So the button was disabled with nothing able to re-enable it.
    //
    // Forcing a choice here is meant to stop customers ordering the WRONG
    // variant. With one variant there is no wrong choice, so the right fix is
    // not to disable the button in the first place rather than to find another
    // way to switch it back on.
    //
    // Counted from the DOM, not from `product-variants.product`: this runs
    // immediately on parse and the custom element may not be upgraded yet,
    // whereas the rendered options are already in the document. A selector that
    // exposes neither radios nor a select counts 0, which also skips - matching
    // what the old code did anyway, since with no radios to uncheck it never
    // reached the disable block.
    let choosable = 0;
    optionSelectors.forEach(optionSelector => {
      const radios = optionSelector.querySelectorAll("input[type='radio']").length;
      const opts = optionSelector.querySelectorAll("select option").length;
      if (Math.max(radios, opts) > 1) choosable++;
    });
    if (choosable === 0) {
      return;
    }

    // Loop through all swatches and if it's not color, uncheck all elements
    optionSelectors.forEach(optionSelector => {
      let optionName = selectorOptionName(optionSelector);

      /*
      if (optionName === "color") {
        return;
      }
      */
  
      // uncheck all elements
      let swatchRadios = optionSelector.querySelectorAll("input[type='radio']");
      swatchRadios.forEach(swatchRadio => {
        swatchRadio.checked = false;
        uncheckedOptions = true;
      });
    });
  
    // if we did uncheck any options, then we can remove the value of the id element
    // this allows us to take advantage of existing code paths in theme.js
    // that will correctly update the add to cart button when a variant is selected
    // If we didn't do this, then the user selecting the same variant as the page default
    // wouldn't trigger variant:changed and the atc button would not be updated
    if(uncheckedOptions) {
      // // if this is breakign things look at pins and aces for an alternative approach where I use a one-time event listener on the swatch list
      // // and manually change the variant id, which causes the themehandler code to emit an event
      // line below was used prior to 9/9/22
      // document.querySelectorAll("input[name=id]").forEach(input => {input.value = null});
  
      // this theme has color-swatch-list as well, but we ignore that because we are ignoring
      // color options for forcing selection
      // Null-guarded: a single-option product whose option renders as a
      // dropdown rather than swatches has neither list, and an unguarded
      // .addEventListener here would throw and abort the rest of this
      // function — leaving Add to Cart enabled, which is the exact bug this
      // is meant to prevent.
      swatch = document.querySelector(".block-swatch-list, .color-swatch-list");
      if (swatch) {
        swatch.addEventListener('click', forceSwatchSelection, {once: true});
      }
  
      
      // If the klaviyo BIS trigger is displayed, don't mess with the ATC button as the theme has already handled it
      if(document.querySelector("#bis") && (document.querySelector("#bis").style.display != "none")) {
        // Back-in-stock form is showing, so leave Add to Cart alone.
      } else {
        // Disable the Add to Cart button. Guarded because a sold-out product
        // renders no #AddToCart at all.
        let atcButton = document.querySelector("#AddToCart");
        if (!atcButton) return;
        atcButton.disabled = true

        /*
        let atcSticky = document.querySelector("#StickyAddToCart");
        atcSticky.disabled = true
        atcSticky.innerText = "Make a Selection";
        */
       
        let atcText = atcButton.querySelector(".loader-button__text")
        if (atcText) atcText.innerText = "Make a Selection";
        atcButton.style.display="block";
        
        // let stickyPrice = document.querySelector(".product-sticky-form__price");
        // stickyPrice.innerText = "";
        // let stickyVariant = document.querySelector(".product-sticky-form__color");
        // stickyVariant.innerText = "";

        // Every option was unchecked above, so relabel every option's value —
        // not just the first. On a two-option product the old single-element
        // version left the second still showing a value nobody had chosen.
        document.querySelectorAll(".product-form__option-value").forEach(label => {
          label.innerText = "Make a Selection";
        });

        /*
        let selectWrappers = document.querySelectorAll(".select-wrapper");
        selectWrappers[0].querySelector(".select__selected-value").innerText = "Select a Model";
        selectWrappers[1].querySelector(".select__selected-value").innerText = "Select an Option";
        */
        
      }
    }
  }
  
  // grab the productVariant obj and use it's product property to get the list of variants
  // if there's more than one, quickly switch to the next variant which will force the theme to update
  // the swatch control and then the 'real' click handler will set it to the clicked element in the swatch.
  // This fixes the bug where on initial page load, if you clicked the first variant in the swatch, nothing would 
  // happen (because it was already the selected variant id even though the swatch html did not indicate that)
  function forceSwatchSelection(event) {
      const productVariantElement = document.querySelector('product-variants');
    
      if(!productVariantElement) return false;
    
      if(productVariantElement.product.variants.length > 1) {
        productVariantElement.selectVariant(productVariantElement.product.variants[1].id);
      }
  }
  
  // return a clean option name for this selector by downcasing and removing any colons
  function selectorOptionName(optionSelector) {
    return optionSelector.querySelector(".product-form__option-name").innerText.toLowerCase().replace(":","");
  }

  if(window.location.href.indexOf("/products/") > 0) {
    forceVariantSelection();
  }


// AfterShip Personalization
const getCart = async () => {
  const requestUrl = "/cart.js";
  const res = await window.fetch(requestUrl, {
    method: "GET",
  });
  const data = await res.json();
  if (!data) {
    return;
  }
  return data
}

const updateCart = async () => {
  const cart = await getCart()
  if (!cart) {
    return;
  }
  document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
    bubbles: true,
    detail: {
      cart,
      openMiniCart: true
    }
  }))
}

// Semicolon is load-bearing: the next block is an IIFE starting with `(`, and
// without it ASI parses this line as a call on addEventListener's return value.
document.addEventListener('apz:added', updateCart);


/* AO - product image caption (replaces the Ethercycle version)

   The caption under the product gallery shows the alt text of whichever image
   is currently on screen. It had been blank on every DISCOUNTED product since
   the `ao-sale-flash` badge shipped: the old code read
   `selectedElement.children[0].children[0].alt`, and the badge is rendered as
   the first child of `.product__media-image-wrapper`, so children[0] stopped
   being the <img>. A <div> has no `.alt`, so the caption was set to ''.
   Products without a sale badge kept working, which is why this looked
   intermittent.

   This version reads the alt off the currently-visible media item instead, so:
     - it does not care where the badge sits in the wrapper;
     - it works on single-media products, where Flickity never initialises at
       all (its constructor returns early on `childElementCount === 1`);
     - it survives Flickity failing to start for any other reason.

   A MutationObserver on the media list is what actually drives it. Flickity
   moves `is-selected` between cells however the customer got there - drag,
   arrows, thumbnail click - and a variant change re-filters the cells, so
   watching the class attribute catches every case with one code path. The
   flickity events are kept as a belt-and-braces first paint.
*/
(function () {
  function mediaList() {
    return document.querySelector('.product__media-list');
  }

  function currentMediaImage() {
    var list = mediaList();
    if (!list) return null;

    // `is-selected` is Flickity's; `is-initial-selected` is written by Liquid and
    // is all we get before (or without) Flickity. Fall back to the first cell
    // that has not been filtered out by the variant picker.
    var item =
      list.querySelector('.product__media-item.is-selected') ||
      list.querySelector('.product__media-item.is-initial-selected') ||
      list.querySelector('.product__media-item:not(.is-filtered)');

    return item ? item.querySelector('img') : null;
  }

  function updateProductImageCaption() {
    var caption = document.querySelector('.flickity-caption');
    if (!caption) return;

    var img = currentMediaImage();
    var alt = img && img.getAttribute('alt') ? img.getAttribute('alt').trim() : '';

    // Non-breaking space when there is nothing to say, so the caption keeps its
    // height and the page does not jump as the customer moves between slides
    // that do and do not have alt text. This matches the `&nbsp;` the Liquid
    // ships in the empty element.
    caption.textContent = alt || '\u00a0';
  }

  function start() {
    var list = mediaList();
    if (!list) return; // not a product page

    updateProductImageCaption();

    new MutationObserver(updateProductImageCaption).observe(list, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    window.addEventListener('flickity:ready', updateProductImageCaption);
    window.addEventListener('flickity:slide-changed', updateProductImageCaption);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
/* AO - Redo Back-in-Stock button de-dupe
   Some configurations can cause Redo's BIS container to be injected twice.
   We keep the instance inside the product form (cart/add form) when present.
*/
(function () {
  function dedupeRedoBackInStock() {
    var nodes = document.querySelectorAll('redo-back-in-stock-container-element');
    if (!nodes || nodes.length <= 1) return;

    var preferred = null;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.closest && el.closest('form[action*="/cart/add"]')) {
        preferred = el;
        break;
      }
    }
    if (!preferred) preferred = nodes[0];

    for (var j = 0; j < nodes.length; j++) {
      if (nodes[j] !== preferred) {
        nodes[j].remove();
      }
    }
  }

  // Run once on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dedupeRedoBackInStock);
  } else {
    dedupeRedoBackInStock();
  }

  // Redo may inject after load; observe DOM changes and re-run (debounced)
  var t = null;
  var observer = new MutationObserver(function () {
    if (t) return;
    t = window.setTimeout(function () {
      t = null;
      dedupeRedoBackInStock();
    }, 50);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
